import { inject, Injectable, signal } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { SpreeService } from '../../../services/spree-client/storefront/spree.service';
import {
  catchError,
  combineLatest,
  filter,
  map,
  merge,
  Observable,
  of,
  shareReplay,
  startWith,
  tap,
} from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { IProducts, ITaxon } from '../../../services/spree-client/storefront';
import { ProductListPage } from './product-list.types';
import { FiltersService, getSelectedFilters } from './filters.service';
import { Filters, FilterType } from '../../../types/filters.type';
import {
  deserializeLimitedVariants,
  deserializeSearchMetadata,
} from './product-list.mapper';
import { SortingOptions, SortOption } from './sort.type';
import { StateService } from '../../../services/state.service';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateRoutePipe } from '../../../pipes/translate-route.pipe';
import { TranslocoService } from '@jsverse/transloco';
import { taxonToCategory } from '../../../serializers/category.serializer';
import { is404 } from '../../../helpers/http';
import { PAGE_SIZE_DEFAULT, SORT_OPTION_DEFAULT } from './product-list.options';

@Injectable()
export class ProductListService {
  private readonly activeRoute = inject(ActivatedRoute);
  private readonly spree = inject(SpreeService);
  private readonly filters = inject(FiltersService);
  private readonly state = inject(StateService);
  private readonly translateRoute = inject(TranslateRoutePipe);
  private readonly t = inject(TranslocoService);

  private readonly taxon$ = this.activeRoute.url.pipe(
    filter((urlSegments) => urlSegments[0]?.path === 't'),
    map((urlSegments) => urlSegments.slice(1).join('/')),
    switchMap((id) =>
      this.spree.taxons.show({ id, include: 'image' }).pipe(
        catchError((e: HttpErrorResponse) => {
          if (e.status === 404) {
            return of(null);
          }

          throw e;
        }),
      ),
    ),
  );

  private readonly search$ = combineLatest({
    url: this.activeRoute.url,
    query: this.activeRoute.queryParams,
  }).pipe(
    filter(
      ({ url, query }) =>
        url[0]?.path === this.translateRoute.transform('search') && !!query['q'],
    ),
    map(({ query }) => query['q']),
    filter(Boolean),
  );

  private readonly trigger$: Observable<ITaxon | string | null> = merge(
    this.search$,
    this.taxon$,
  );

  isLoading = signal(true);
  isTaxonLoading = signal(true);

  readonly products$: Observable<ProductListPage | null> = combineLatest([
    this.trigger$,
    this.activeRoute.queryParams,
  ]).pipe(
    tap(() => this.isLoading.set(true)),
    switchMap(([taxonOrSearch, query]) => {
      if (taxonOrSearch == null) {
        return of(null);
      }

      return (
        typeof taxonOrSearch === 'string'
          ? this.getSearchResults(taxonOrSearch, query)
          : this.getFiltersAndProducts(taxonOrSearch, query)
      ).pipe(
        catchError((e: unknown) => {
          if (!is404(e)) {
            throw e;
          }

          return of({
            products: new Array(PAGE_SIZE_DEFAULT),
            meta: {
              sorting: SORT_OPTION_DEFAULT,
            },
          } as ProductListPage);
        }),
      );
    }),
    tap(() => {
      this.isLoading.set(false);
      this.isTaxonLoading.set(false);
    }),
    startWith({
      products: new Array(PAGE_SIZE_DEFAULT),
      meta: {
        sorting: SORT_OPTION_DEFAULT,
      },
    } as ProductListPage),
    shareReplay(1),
  ) as Observable<ProductListPage | null>;

  private getSearchResults(
    searchQuery: string,
    query: Params,
  ): Observable<ProductListPage> {
    const page = extractPageIndex(query);

    // todo refactor getProducts params
    return this.getProducts({ name: searchQuery }, undefined, undefined, page).pipe(
      map((products) => {
        const productList = this.mapProductsFetchResult(products);

        productList.meta = {
          ...productList.meta,
          category: {
            id: 'search',
            name: 'search',
            metaTitle: `${this.t.translate('Search results')}`,
            metaDescription: '',
            description: '',
            prettyName: '',
            permaLink: '',
          },
        };

        return productList;
      }),
    );
  }

  private getFiltersAndProducts(
    taxon: ITaxon,
    query: Params,
  ): Observable<ProductListPage> {
    const page = extractPageIndex(query);
    const sorting = extractSorting(query);
    const category = taxonToCategory(taxon);

    return this.filters.getForTaxon(taxon, query).pipe(
      switchMap((filters) => {
        const selectedFilters = getSelectedFilters(filters);

        return this.getProducts(
          { taxons: taxon.data.id },
          selectedFilters,
          sorting,
          page,
        ).pipe(
          map((products) => {
            const productList = this.mapProductsFetchResult(products, filters, sorting);

            productList.meta = {
              ...productList.meta,
              category,
            };

            return productList;
          }),
        );
      }),
    );
  }

  private getProducts(
    filter: { [key: string]: string | number },
    propertiesAndOptions?: Filters,
    sorting?: SortOption,
    page = 1,
  ): Observable<IProducts> {
    return this.spree.products.list({
      filter,
      ...this.filtersToSpreeRequestObject(propertiesAndOptions),
      fields: {
        product: 'sku,name,slug,default_variant,public_metadata',
        variant:
          'sku,price,display_price,product,images,public_metadata,compare_at_price',
      },
      include: 'default_variant',
      sort: sorting,
      page,
      currency: this.state.getActiveCurrency(),
      per_page: PAGE_SIZE_DEFAULT,
    });
  }

  private filtersToSpreeRequestObject(filters?: Filters): Record<string, string> {
    if (!filters) {
      return {};
    }

    const result = {} as Record<string, string>;

    const mapper = (filterType: 'options' | 'properties', type: FilterType): void => {
      result[`filter[${filterType}][${type.name}]`] = type.values
        .map((v) => v.name)
        .join(',');
    };

    filters.optionTypes.forEach((option) => mapper('options', option));
    filters.productTypes.forEach((property) => mapper('properties', property));

    return result;
  }

  private mapProductsFetchResult = (
    products: IProducts,
    filters = {} as Filters,
    sorting?: SortOption,
  ): ProductListPage => {
    return {
      products: deserializeLimitedVariants(products),
      meta: {
        ...deserializeSearchMetadata(products.meta),
        filters,
        sorting,
      } as ProductListPage['meta'],
    };
  };
}

function extractPageIndex(params: Params): number {
  return parseInt(params['page'] ?? '1');
}

function extractSorting(params: Params): SortOption {
  const raw = params['sort'];

  return SortingOptions.includes(raw) ? raw : SORT_OPTION_DEFAULT;
}
